import { useSelector, useDispatch } from 'react-redux';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { CHOOSE_CAMPAIGN_COLUMNS } from 'modules/experiments/types';
import { SkeletonRow } from 'modules/experiments/components/pages/index/sections/table/SkeletonRow';
import { NoData } from 'modules/experiments/components/ui/emptyState/NoData';
import {
  CHOOSE_CAMPAIGN_TABLE_GRID,
  DEFAULT_ROW_COUNT,
  chooseCampaignColumns,
} from 'modules/experiments/constants';
import {
  getChooseCampaignSearch,
  getChooseCampaignSortBy,
  getChooseCampaignSortDirection,
  getChooseCampaignTableCampaigns,
  getChooseCampaignTableLoading,
} from 'modules/experiments/ducks/selectors';
import {
  refetchCampaigns,
  setChooseCampaignSortBy,
  setChooseCampaignSortDirection,
} from 'modules/experiments/ducks/actions';
import { Campaign } from 'modules/domains/campaign';
import { COMPONENT_TYPE } from 'modules/experiments/components/ui/emptyState';
import { CampaignsEmptyState, NoResult } from 'ui/illustrations';
import { useExperimentsTranslations } from 'modules/experiments/hooks';

import { HeaderRow } from './HeaderRow';
import { TableContentWrapper } from '../styled';
import { DataRow } from './DataRow';
import { ChooseCampaignPagination } from '../ChooseCampaignPagination';

export const ChooseCampaignTable = () => {
  const dispatch = useDispatch();
  const { t } = useExperimentsTranslations();
  const sortBy = useSelector(getChooseCampaignSortBy);
  const sortDirection = useSelector(getChooseCampaignSortDirection);
  const search = useSelector(getChooseCampaignSearch);
  const setSortBy = (col: CHOOSE_CAMPAIGN_COLUMNS) => {
    dispatch(setChooseCampaignSortBy(col));
    dispatch(refetchCampaigns());
  };
  const setSortDirection = (direction: SORT_DIRECTIONS) => {
    dispatch(setChooseCampaignSortDirection(direction));
    dispatch(refetchCampaigns());
  };
  const campaigns = useSelector(getChooseCampaignTableCampaigns);
  const loading = useSelector(getChooseCampaignTableLoading);
  const skeletonColumnCount = Object.keys(chooseCampaignColumns).length;

  const hasCampaigns = campaigns.length > 0;
  const hasSearch = search.length > 0;
  const tableContent = () => {
    switch (true) {
      case loading:
        return [...Array(DEFAULT_ROW_COUNT)].map((_, i) => (
          <SkeletonRow
            key={i}
            tableGrid={CHOOSE_CAMPAIGN_TABLE_GRID}
            skeletonColumnCount={skeletonColumnCount}
          />
        ));
      case hasCampaigns:
        return campaigns.map((campaign: Campaign) => (
          <DataRow
            key={`campaign-row-${campaign.id}`}
            campaign={campaign}
            tableGrid={CHOOSE_CAMPAIGN_TABLE_GRID}
          />
        ));
      case !hasCampaigns && hasSearch:
        return (
          <NoData
            componentType={COMPONENT_TYPE.TAKEOVER_TABLE}
            Icon={NoResult}
            title={t('chooseCampaign.noData.noMatchingResults')}
            subTitle={t('chooseCampaign.noData.noMatchingResultsSubtitle')}
            testId={'choose-campaign-no-results'}
          />
        );
      case !hasCampaigns:
        return (
          <NoData
            componentType={COMPONENT_TYPE.TAKEOVER_TABLE}
            Icon={CampaignsEmptyState}
            title={t('chooseCampaign.noData.noCampaigns')}
            subTitle={t('chooseCampaign.noData.noCampaignsSubtitle')}
            link={'https://support.iterable.com/'}
            linkText={t('chooseCampaign.noData.noCampaignsLinkText')}
            testId={'choose-campaign-empty-state'}
            newTabLink
          />
        );
      default:
        return (
          <NoData
            componentType={COMPONENT_TYPE.TAKEOVER_TABLE}
            Icon={CampaignsEmptyState}
            title={t('chooseCampaign.noData.noCampaigns')}
            subTitle={t('chooseCampaign.noData.noCampaignsSubtitle')}
            link={'https://support.iterable.com/'}
            linkText={t('chooseCampaign.noData.noCampaignsLinkText')}
            testId={'choose-campaign-empty-state'}
            newTabLink
          />
        );
    }
  };

  return (
    <div data-test="choose-campaign-table">
      <HeaderRow
        sortedBy={sortBy}
        setSortedBy={setSortBy}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        columns={chooseCampaignColumns}
      />
      <TableContentWrapper>
        {tableContent()}
        {hasCampaigns && <ChooseCampaignPagination />}
      </TableContentWrapper>
    </div>
  );
};
